/* eslint-disable jsx-a11y/anchor-is-valid */
import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import HeaderImage from "../assets/head.png";
import { login } from "../services/apiService/authApi";
import { useDispatch, useSelector } from "react-redux";
import { setIsAuthenticated, setToken } from "../store/slices/authSlice";
import { setDataSyncTime } from "../store/slices/userSlice";
import { setUser, setPreferences } from "../store/slices/userSlice";
import useToaster from "../hooks/useToaster";
import LoadingPage from "../components/edgeCases/LoadingPage";
import { useRef } from "react";
import { API_BASE_URL } from "../services/apiService/axiosInstance";

/**
 * @component LoginPage
 * @description Renders a login page for user authentication, handling form submission and redirecting to the main app upon success.
 * @returns {JSX.Element}
 */
const LoginPage = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const showToast = useToaster();
  const { isAuth } = useSelector((state) => state.auth);
  const { userData } = useSelector((state) => state.user);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showLoadingPage, setShowLoadingPage] = useState(false);
  const [loggedInUserName, setLoggedInUserName] = useState("");
  const lastFailedCredentials = useRef({ email: "", password: "", error: "" });

  // Redirect if already authenticated
  useEffect(() => {
    if (isAuth && userData && Object.keys(userData).length > 0 && !showLoadingPage) {
      navigate("/myTask", { replace: true });
    }
  }, [isAuth, userData, showLoadingPage, navigate]);

  /**
   * @description Handles form submission for user login, authenticates via API, and updates Redux state.
   * @async
   * @param {React.FormEvent} e - The form submission event.
   */
  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);

    if (!navigator.onLine) {
      showToast("You are currently offline. Please check your internet connection !");
      setIsSubmitting(false);
      return;
    }

    const sameAsLastFailed =
      lastFailedCredentials.current.email === email &&
      lastFailedCredentials.current.password === password &&
      lastFailedCredentials.current.error;

    if (sameAsLastFailed) {
      showToast(lastFailedCredentials.current.error);
      setIsSubmitting(false);
      return;
    }

    try {
      const response = await login({ email, password });

      const formatDate = (inputDate) => {
        const date = new Date(inputDate);
        const pad = (n) => n.toString().padStart(2, "0");

        return (
          `${pad(date.getMonth() + 1)}/${pad(
            date.getDate()
          )}/${date.getFullYear()} ` +
          `${pad(date.getHours())}:${pad(date.getMinutes())}:${pad(
            date.getSeconds()
          )}`
        );
      };

      if (response?.data) {
        lastFailedCredentials.current = { email: "", password: "", error: "" };
        const { userData, tokens } = response.data;
        let data = response?.data?.date?.[0];

        let dataSyncValue = {
          id: data.id,
          value: formatDate(data.date),
        };

        dispatch(setIsAuthenticated(true));
        dispatch(setToken(tokens));
        dispatch(setUser(userData));
        dispatch(setPreferences(userData?.preferences || []));
        dispatch(setDataSyncTime(dataSyncValue));
        setShowLoadingPage(true);
      } else {
        const errorMessage =
          response?.error || "Login failed. Please try again.";

        showToast(errorMessage);

        lastFailedCredentials.current = {
          email,
          password,
          error: errorMessage,
        };
      }
    } catch (err) {
      const errorMessage =
        err?.response?.message || err.message || "Something went wrong.";

      showToast(errorMessage);

      lastFailedCredentials.current = { email, password, error: errorMessage };
    } finally {
      setIsSubmitting(false);
    }
  };

  /**
   * @description Conditionally renders the loading page or the login form.
   * @returns {JSX.Element}
   */
  if (showLoadingPage) {
    return (
      <LoadingPage
        onComplete={() => navigate("/myTask", { replace: true })}
      />
    );
  }

  return (
    <div className="bg-gradient-to-r from-blue-100 to-purple-100 flex flex-col min-h-screen">
      <div className="flex justify-center mt-[30px]">
        <img src={HeaderImage} alt="Scooter Logo" className="h-40 w-90" />
      </div>

      <main className="flex flex-col md:flex-row items-center justify-between flex-grow max-w-6xl mx-auto p-6 w-full mt-[-20px]">
        <div className="text-center md:text-left mb-8 md:mb-0 md:w-1/2">
          <h1 className="text-5xl font-bold text-blue-700">
            Welcome to <br />
            <span className="text-blue-800">Scooter</span>
          </h1>
          <p className="text-gray-600 mt-2">
            Your central hub for seamless Cell Press production tracking.
          </p>
          <div className="bg-blue-200 bg-opacity-50 p-6 mt-6 rounded-lg shadow-lg border-l-4 border-blue-600">
            <h2 className="text-blue-800 font-semibold">
              Streamlined Tracking
            </h2>
            <p className="text-blue-600 mt-2">
              Our system simplifies the entire Cell Press production tracking
              process from submission to publication.
            </p>
          </div>
        </div>

        <div className="bg-white p-8 rounded-lg shadow-xl w-full max-w-md md:w-1/2 md:ml-auto">
          <h2 className="text-2xl font-bold text-blue-700 mb-6 text-center">
            Sign In
          </h2>
          {/* {error && <p className="text-red-600 text-center">{error}</p>} */}
          <form onSubmit={handleSubmit}>
            <div className="mb-4">
              <label className="block text-gray-700" htmlFor="email">
                Email address
              </label>
              <input
                className="w-full p-2 border border-gray-300 rounded mt-1"
                id="email"
                type="email"
                placeholder="Email address"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
              />
            </div>
            <div className="mb-4">
              <label className="block text-gray-700" htmlFor="password">
                Password
              </label>
              <input
                className="w-full p-2 border border-gray-300 rounded mt-1"
                id="password"
                type="password"
                placeholder="Password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
              />
            </div>
            <div className="flex justify-end items-center mb-6">
              <a className="text-blue-600 hover:underline" href="#">
                Forgot Password?
              </a>
            </div>
            <button
              className={`w-full bg-blue-600 text-white py-2 rounded hover:bg-blue-700 transition duration-200 ${
                isSubmitting ? "opacity-50 cursor-not-allowed" : ""
              }`}
              type="submit"
              disabled={isSubmitting}
            >
              {isSubmitting ? "Sign In" : "Sign In"}
            </button>
            <button
              className={`w-full bg-green-600 text-white py-2 rounded hover:bg-green-700 mt-3 transition duration-200`}
              type="button"
              onClick={() => {
                if (!navigator.onLine) {
                  showToast("You are currently offline. Please check your internet connection !");
                  return;
                }
                window.location.href = `${API_BASE_URL}/login`;
              }}
            >
              Login With SSO
            </button>
          </form>
        </div>
      </main>

      <footer className="text-center text-gray-600 py-4">
        <p>© 2025 Scooter. All rights reserved.</p>
        <div className="mt-2">
          <a className="hover:underline mx-2" href="#">
            Privacy Policy
          </a>
          <a className="hover:underline mx-2" href="#">
            Terms of Service
          </a>
          <a className="hover:underline mx-2" href="#">
            Contact Us
          </a>
        </div>
      </footer>
    </div>
  );
};

export default LoginPage;
